import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { suppressGlobalErrorNavigation } from '../../core/interceptors/error/suppress-global-error';
import { DataResponse } from '../../core/models/data-response';
import { SortDirectionType } from '../../core/models/sort-direction';
import { ConfigService } from '../../core/services/config/config.service';
import { UtilityService } from '../../core/services/utility/utility.service';
import { PatientFilter } from '../../shared/models/patient-filter';
import { PatientAccessRecord, PatientAccessSearchResult } from '../models/patient-access-record';

/**
 * Records requested per search. There is no paging UI on the results screen, so the screen reports
 * the total alongside the page rather than presenting one page as the complete match set.
 * Matches `PATIENT_SEARCH_PAGE_SIZE` on the linking-request searches.
 */
export const PATIENT_ACCESS_PAGE_SIZE = 25;

/**
 * The only column the endpoint's sort whitelist accepts.
 *
 * As of 2026-09-23, per `Logic/Validation/PatientSearchRequestValidator.cs`, whose `SortColumnNames`
 * is `[nameof(PatientSummaryDto.DisplayName)]` — anything else is a 400, not a fallback sort.
 */
export const PATIENT_ACCESS_SORT_COLUMN = 'DisplayName';

/**
 * Populated dialog fields required before a search is submitted.
 *
 * This mirrors the server's own rule rather than sitting apart from it. As of 2026-09-23,
 * `PatientSearchRequestValidator.HasAtLeastTwoSearchCriteria` requires **two** populated criteria
 * and counts `firstName`, `lastName`, `dateOfBirth` and `medicalRecordNumber` each once. Because
 * this screen sends the two names as their own fields (see {@link PatientAccessSearchRequest}), a
 * name-only search arrives as two criteria and clears both gates.
 *
 * Note the consequence for single-field lookups: an MRN on its own is one criterion and the API
 * rejects it, however precise it is. Relaxing this gate alone would not help — it would turn a
 * blocked search into a 400. Letting MRN stand alone is a backend change first (see the PR thread
 * on this constant).
 */
export const PATIENT_ACCESS_MINIMUM_CRITERIA = 2;

/**
 * Request body for `POST /Patients/Search` (the API's `PatientSearchRequest`).
 *
 * The API also accepts a combined `searchTerm` ("first last", matched against the
 * `FirstName + " " + LastName` concatenation). This screen deliberately does not send it: the
 * validator credits `searchTerm` as a **single** criterion however many names it contains, so a
 * name-only search composed into one term is rejected by the two-criteria rule. Sent as the pair
 * below, the same search is two criteria and is also order-independent. It is omitted from this
 * interface rather than sent as null so it cannot be quietly populated back.
 */
export interface PatientAccessSearchRequest {
  /**
   * Partial match against the first-name column alone (`Contains`), never across the two name
   * columns — a value containing a space matches nothing. Minimum three characters once trimmed.
   */
  firstName: string | null;
  /** Partial match against the last-name column alone. See {@link PatientAccessSearchRequest.firstName}. */
  lastName: string | null;
  /** ISO `YYYY-MM-DD` — the API binds a `DateOnly`, so a full timestamp is not accepted. */
  dateOfBirth: string | null;
  medicalRecordNumber: string | null;
  sortDirection: SortDirectionType;
  sortColumn: string;
  pageNumber: number;
  pageSize: number;
}

/**
 * Patient lookup behind the Access & Controls screens — the "Search by Patient" dialog and the
 * per-patient detail screen it leads to.
 *
 * Unlike the linking-request patient searches this one is not anchored to a `PatientPortalUserRequestId`:
 * `POST /Patients/Search` reads the portal-owned patient table, writes no search note and files no
 * audit entries, which is what makes it usable from access management, where there is no request to
 * attribute a search to. `GET /Patients/{patientId}` reads the same table and is likewise
 * unanchored. A patient's portal access state is a separate concern — see
 * `PatientPortalAccessService`.
 */
@Injectable({
  providedIn: 'root',
})
export class PatientAccessSearchService {
  private readonly utilityService = inject(UtilityService);
  private readonly configService = inject(ConfigService);

  /**
   * Read per call rather than captured at construction: an eager field makes merely *creating*
   * the service throw `ConfigNotLoadedError` when config is unavailable, which reaches callers
   * that never intended to search.
   */
  private get patientsEndpoint(): string {
    return `${this.configService.config.adminDashboardApiUrl}/Patients`;
  }

  /**
   * Patients matching every populated field on `filter`, capped at
   * {@link PATIENT_ACCESS_PAGE_SIZE} with the full match count alongside.
   *
   * Returns `totalRecordCount` rather than just the page: the screen requests a fixed page size
   * with no paging UI, so without the total it would render a truncated page as the complete match
   * set — and "no match" is what leads staff to conclude a patient has no record.
   *
   * Opts out of the global error navigation: the results screen renders its own failure banner, and
   * `ErrorInterceptor`'s default hop to `/error` would tear the component down before it could. A
   * 401 still reaches the sign-in flow — the interceptor makes that case non-suppressible.
   */
  searchPatients(filter: PatientFilter): Observable<PatientAccessSearchResult> {
    const request: PatientAccessSearchRequest = {
      firstName: filter.firstName,
      lastName: filter.lastName,
      dateOfBirth: filter.dateOfBirth,
      medicalRecordNumber: filter.medicalRecordNumber,
      sortDirection: SortDirectionType.Ascending,
      sortColumn: PATIENT_ACCESS_SORT_COLUMN,
      pageNumber: 1,
      pageSize: PATIENT_ACCESS_PAGE_SIZE,
    };

    return this.utilityService
      .postRequest<
        DataResponse<PatientAccessRecord[]>
      >(`${this.patientsEndpoint}/Search`, request, false, false, true, suppressGlobalErrorNavigation())
      .pipe(
        map((dataResponse) => ({
          // Defaulted rather than trusted: `result` is *typed* as an array, but that is a claim
          // about the payload, not a guarantee. A 200 carrying `"result": null` would otherwise
          // reach the template and throw on `.length`, turning a degenerate response into a blank
          // screen instead of the empty-match state.
          patients: dataResponse.result ?? [],
          totalRecordCount: dataResponse.totalRecordCount,
        })),
      );
  }

  /**
   * One patient's identity record, for the access-detail screen reached from a results row.
   *
   * It lives beside the search rather than on a service of its own: both read the same
   * portal-owned patient table, and the detail screen is only ever reached from a results row.
   *
   * The endpoint returns the same identity-only `PatientSummaryDto` the search does — no
   * portal-access state and no linked accounts. Those come from `PatientPortalAccessService`.
   *
   * Opts out of the global error navigation for the same reason the search does: the detail screen
   * distinguishes "Patient not found" from "Unable to load patient", and `ErrorInterceptor`'s hop
   * to `/error` would tear the component down before either panel could render.
   */
  getPatientById(patientId: number): Observable<PatientAccessRecord> {
    return this.utilityService.getRequest<PatientAccessRecord>(
      `${this.patientsEndpoint}/${patientId}`,
      false,
      suppressGlobalErrorNavigation(),
    );
  }
}
